'use strict';

/**
 * @fileoverview intlLocaleFor — the ONE UI-language → Intl-locale mapping
 * (ECO-RESIDUALS-CLEANUP-001 R3, 2026-07-30 · dual-use ES-I18N-FOLLOW-ON Q-20, 2026-08-02).
 *
 * THIS FILE IS THE IMPLEMENTATION; the ESM twin re-exports it. Same treatment, and same
 * reason, as `formatterCache` in Lane A2: an API-tier consumer needs the mapping from
 * CommonJS. Q-20's shared tenant-locale resolver maps a tenant's configured LANGUAGE to an
 * Intl LOCALE, and re-deriving that map in the API tier would fork the seam this module
 * exists to be — so the boundary moves instead of the map.
 *
 * Before this seam existed, every `Intl.DateTimeFormat` / `Intl.NumberFormat`
 * in the ecosystem hardcoded `'en-US'`, so a Spanish-language user saw
 * English-formatted dates inside translated pages. This module lives in
 * i18n-bootstrap because the mapping is a property of the LANGUAGE SYSTEM —
 * it must stay in lockstep with `SUPPORTED_LANGUAGES`; temporal-core is
 * deliberately locale-free, and page-shell is web-only UI.
 *
 * WHY `es → 'es-US'` (not es-MX / es-ES): this is a US-market product.
 * `es-US` keeps US number and currency conventions — comma thousands, period
 * decimal, leading `$` — so every `Intl.NumberFormat` output is
 * BYTE-IDENTICAL in both supported languages (money and metrics do not
 * visually shift under a language toggle), while
 * `Intl.DateTimeFormat('es-US')` correctly yields Spanish month/day names and
 * es date ordering. `es-ES` would flip digit grouping (`1.234,56`) for zero
 * product benefit. The frozen map below is the one place a future
 * language→locale pair is added.
 */

/** Frozen SSOT: supported UI language → Intl locale. */
const INTL_LOCALE_BY_LANGUAGE = Object.freeze({
  en: 'en-US',
  es: 'es-US',
});

const FALLBACK_LOCALE = INTL_LOCALE_BY_LANGUAGE.en;

/**
 * Resolve the active UI language (i18next `i18n.language` — short tag or
 * BCP-47) to the Intl locale every formatter should use.
 *
 * Tolerates non-string/empty input (provider-less test renders yield
 * `i18n.language === undefined`) — falls back to `'en-US'`.
 *
 * @param {string} [language] Active UI language ('en', 'es', 'es-MX', …).
 * @returns {string} Intl locale ('en-US' | 'es-US').
 */
function intlLocaleFor(language) {
  const primary =
    typeof language === 'string' && language.length > 0
      ? language.split('-')[0].toLowerCase()
      : 'en';
  return INTL_LOCALE_BY_LANGUAGE[primary] || FALLBACK_LOCALE;
}

module.exports = { intlLocaleFor, INTL_LOCALE_BY_LANGUAGE };
